import APITemplate from '@api/APITemplate'
import { diffTimeMinutes, formatQueryParams } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { categories } from '@modules/financial-planning/constants/URL'

function getQueryString(params) {
  const query = formatQueryParams(params || {})
  return query === '?' ? '' : query
}

class APIFinancialPlanningCategories extends APITemplate {
  constructor() {
    super()
    this.reqCarregar = null
  }

  _shouldSkipLoad(forceUpdate) {
    if (!forceUpdate) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['financialPlanning/categoriesLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )

      if (!shouldUpdate) {
        return store.getters['financialPlanning/categories']
      }
    }

    if (store.getters['financialPlanning/categoriesLoading']) {
      return store.getters['financialPlanning/categories']
    }

    return null
  }

  _handleResponse(response) {
    if (response && response.status === 200) {
      const items = Array.isArray(response.value) ? response.value : []
      store.dispatch('financialPlanning/setCategories', items)
    } else {
      store.dispatch('financialPlanning/setCategories', [])
    }
  }

  async _executeCarregar(requestUrl) {
    store.dispatch('financialPlanning/setCategoriesLoading', true)
    this.reqCarregar = con.get(requestUrl)

    try {
      const response = await this.reqCarregar
      this._handleResponse(response)
      return response
    } catch (error) {
      this._handleResponse(null)
      throw error
    } finally {
      store.dispatch('financialPlanning/setCategoriesLoading', false)
      this.reqCarregar = null
    }
  }

  async carregar({ params = {}, forceUpdate = false } = {}) {
    const skipResult = this._shouldSkipLoad(forceUpdate)
    if (skipResult) return skipResult

    if (this.reqCarregar) {
      return this.reqCarregar
    }

    const query = getQueryString(params)
    const requestUrl = `${categories.list()}${query}`
    const response = await this._executeCarregar(requestUrl)

    return response || {}
  }

  async salvar({ category }) {
    if (!category || typeof category !== 'object') {
      return {
        status: 400,
        message: 'Dados da categoria inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(category),
    }

    const response = await con.post(categories.list(), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/addCategory', value)
      } else {
        await this.carregar({ forceUpdate: true })
      }
    }

    return response || {}
  }

  async atualizar({ categoryId, category }) {
    if (!categoryId) {
      return {
        status: 404,
        message: 'Categoria não encontrada.',
      }
    }

    if (!category || typeof category !== 'object') {
      return {
        status: 400,
        message: 'Dados da categoria inválidos.',
      }
    }

    const payload = {
      data: JSON.stringify(category),
    }

    const response = await con.put(categories.detail(categoryId), payload)

    if (response && response.status === 200) {
      const value = Array.isArray(response.value)
        ? response.value[0]
        : response.value

      if (value && typeof value === 'object') {
        store.dispatch('financialPlanning/updateCategory', value)
      } else {
        await this.carregar({ forceUpdate: true })
      }
    }

    return response || {}
  }

  async remover({ categoryId }) {
    if (!categoryId) {
      return {
        status: 404,
        message: 'Categoria não encontrada.',
      }
    }

    store.dispatch('financialPlanning/setCategoriesLoading', true)

    const response = await con.delet(categories.detail(categoryId))

    store.dispatch('financialPlanning/setCategoriesLoading', false)

    if (response && response.status === 200) {
      store.dispatch('financialPlanning/removeCategory', categoryId)
    }

    return response || {}
  }

  async importar({ table, file, save = true } = {}) {
    const payload = { save }

    if (Array.isArray(table)) {
      payload.table = table
    }

    if (file) {
      payload.file = file
    }

    const response = await con.post(categories.batch(), payload)

    if (response && response.status === 200) {
      await this.carregar({ forceUpdate: true })
    }

    return response || {}
  }

  async batchDelete({ ids, force = false } = {}) {
    if (!Array.isArray(ids) || ids.length === 0) {
      return {
        status: 400,
        message: 'Nenhuma categoria selecionada para exclusão.',
      }
    }

    // BE contract (DELETE /categories/batch):
    //   body: { ids: [uuid, ...], force: bool }
    const payload = { ids, force: !!force }
    const response = await con.delet(categories.batchDelete(), payload)

    if (response && response.status === 200) {
      await this.carregar({ forceUpdate: true })
    }

    return response || {}
  }

  async duplicar({ categoryId, nome } = {}) {
    if (!categoryId) {
      return {
        status: 400,
        message: 'Categoria de origem não informada.',
      }
    }

    if (!nome || typeof nome !== 'string' || !nome.trim()) {
      return {
        status: 400,
        message: 'Nome da nova categoria é obrigatório.',
      }
    }

    // BE contract (POST /categories/<uuid>/duplicate):
    //   body: { data: { nome } }
    const payload = { data: { nome: nome.trim() } }
    const response = await con.post(categories.duplicate(categoryId), payload)

    if (response && response.status === 200) {
      await this.carregar({ forceUpdate: true })
    }

    return response || {}
  }

  /**
   * Semeia os templates padrão de categorias para o escritório.
   * Chamar 1x no onboarding do escritório (substitui o seed-on-read removido
   * do GET /categories após a refatoração de hierarchical-categories-ynab-style).
   * Idempotente: chamadas repetidas não duplicam.
   */
  async ensureDefaultTemplates() {
    const response = await con.post(categories.defaultTemplates(), {})
    if (response && response.status === 200) {
      await this.carregar({ forceUpdate: true })
    }
    return response || {}
  }

}

const apiFinancialPlanningCategories = new APIFinancialPlanningCategories()
export { apiFinancialPlanningCategories }
